import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { fetchDriveItemContent } from "../../services/drive.service";

interface TranscriptViewerProps {
    item: any;
}

const speakerColors = [
    "#E0F2F7", "#E8F5E9", "#FFF3E0", "#F0E6F0", "#FCE4EC",
    "#E8EAF6", "#F5F5DC", "#E1F5FE", "#F8F3EA", "#E8F2E9",
];

export default function TranscriptViewer({ item }: TranscriptViewerProps) {
    const [transcriptArr, setTranscriptArr] = useState<any[]>([]);
    const [nameMapping, setNameMapping] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const loadTranscript = async () => {
            setIsLoading(true);
            setError("");
            try {
                let fetchedData: any = null;

                if (item.fileUrl) {
                    const response = await fetch(item.fileUrl);
                    if (!response.ok) throw new Error("Failed to fetch transcript file");
                    fetchedData = await response.json();
                }
                else if (item._id) {
                    const res: any = await fetchDriveItemContent(item._id);
                    if (res && !res.hasError && res.content) {
                        fetchedData = res.content;
                    } else {
                        throw new Error("Failed to fetch transcript from database.");
                    }
                }

                if (fetchedData) {
                    // --- BULLETPROOF EXTRACTION LOGIC ---
                    const targetPayload = fetchedData.data || fetchedData;

                    let extractedArr: any[] = [];
                    if (Array.isArray(targetPayload)) {
                        extractedArr = targetPayload;
                    } else if (targetPayload && typeof targetPayload === 'object') {
                        extractedArr = targetPayload.transcription || targetPayload.transcriptions || fetchedData.transcriptions || [];
                    }

                    // Extract mapping
                    const extractedMapping = targetPayload.nameMapping || targetPayload.speakers || fetchedData.nameMapping || fetchedData.speakers || {};

                    setTranscriptArr(extractedArr);
                    setNameMapping(extractedMapping);
                } else {
                    setError("No transcript data found.");
                }
            } catch (err: any) {
                console.error("Transcript load error:", err);
                setError(err.message || "Failed to load transcript data.");
            } finally {
                setIsLoading(false);
            }
        };

        if (item) loadTranscript();
    }, [item]);

    // Apply mapping to determine unique speakers for color generation
    const uniqueSpeakers = useMemo(() => {
        return [...new Set(transcriptArr.map((t) => {
            // FIX: Added t.speaker_name to support the flat JSON array format
            const rawSpeaker = t.speaker || t.speaker_name || t.sender || "Speaker";
            return nameMapping[rawSpeaker] || rawSpeaker;
        }))];
    }, [transcriptArr, nameMapping]);

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!transcriptArr || transcriptArr.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
                <Typography color="text.secondary">Transcript is empty or formatted incorrectly.</Typography>
            </Box>
        );
    }

    return (
        <Box
            // Ensures padding doesn't push content off the screen
            sx={{
                height: "100%",
                overflowY: "auto",
                bgcolor: "#fff",
                boxSizing: "border-box"
            }}
        >
            <Box sx={{ maxWidth: "800px", margin: "0 auto", p: 3, pb: 8 }}>
                {transcriptArr.map((elem, index) => {
                    // FIX: Added elem.speaker_name
                    const rawSpeaker = elem.speaker || elem.speaker_name || elem.sender || "Speaker";
                    const speakerName = nameMapping[rawSpeaker] || rawSpeaker;

                    // FIX: Added elem.sentence
                    const textContent = elem.text || elem.sentence || "";

                    const speakerIdx = uniqueSpeakers.indexOf(speakerName);
                    const colorIndex = speakerIdx >= 0 ? speakerIdx : 0;
                    const backgroundColor = speakerColors[colorIndex % speakerColors.length];

                    return (
                        <Box
                            key={elem._id || elem.id || index}
                            sx={{
                                background: backgroundColor,
                                borderRadius: 1,
                                p: 1.25,
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: "inherit",
                                }}
                            >
                                {speakerName}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "inherit",
                                }}
                            >
                                {textContent}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}